import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ClientAccountingHistory - ERP Phase 4
 * 
 * Accounting history for a client - editable in edit mode.
 * Used in admin client detail page (English UI).
 */
const ClientAccountingHistory = ({ clientId, isEditMode = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [genModal, setGenModal] = useState(null); // { month, concept, total }
  const [invBusy, setInvBusy] = useState(false);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const [histRes, invRes] = await Promise.all([
        axios.get(`${API}/accounting/client/${clientId}/history`),
        axios.get(`${API}/invoices?client_id=${clientId}`)
      ]);
      setData(histRes.data);
      setInvoices(invRes.data || []);
    } catch (error) {
      console.error('Error fetching accounting history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchHistory();
    }
  }, [clientId]);

  const formatMonth = (monthStr) => {
    const [year, monthNum] = monthStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-300',
      paid: 'bg-green-500/20 text-green-300',
      issue: 'bg-red-500/20 text-red-300'
    };
    const labels = {
      pending: 'Pending',
      paid: 'Paid',
      issue: 'Issue'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-500/20 text-gray-300'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const startEditing = (record) => {
    setEditingRow(record.month);
    setEditFormData({
      status: record.status || 'pending',
      payment_date: record.payment_date || '',
      notes: record.notes || ''
    });
  };

  const cancelEditing = () => {
    setEditingRow(null);
    setEditFormData({});
  };

  const saveEditing = async (month) => {
    try {
      setSaving(true);
      // Send empty strings to clear fields, backend handles conversion to null
      const saveData = {
        status: editFormData.status,
        payment_date: editFormData.payment_date || '',  // Empty string to clear
        notes: editFormData.notes || ''  // Empty string to clear
      };
      await axios.put(`${API}/accounting/${clientId}/${month}`, saveData);
      await fetchHistory();
      setEditingRow(null);
      setEditFormData({});
    } catch (error) {
      console.error('Error saving accounting record:', error);
      alert('Error saving: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // ERP Phase 4: Delete accounting record
  const deleteRecord = async (month) => {
    if (!window.confirm(`Delete payment record for ${formatMonth(month)}? This cannot be undone.`)) {
      return;
    }
    
    try {
      setSaving(true);
      await axios.delete(`${API}/accounting/${clientId}/${month}`);
      await fetchHistory();
    } catch (error) {
      console.error('Error deleting accounting record:', error);
      alert('Error deleting: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const createNewMonth = async () => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    try {
      setSaving(true);
      await axios.put(`${API}/accounting/${clientId}/${currentMonth}`, {
        status: 'pending',
        notes: ''
      });
      await fetchHistory();
    } catch (error) {
      console.error('Error creating month record:', error);
      alert('Error: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const invoiceForMonth = (month) => invoices.find((iv) => iv.payment_month === month);

  const openGenerate = (record) => {
    const [y, m] = record.month.split('-');
    const monthNamesEs = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const concept = `Entrenamiento Personal – ${monthNamesEs[parseInt(m) - 1]} ${y}`;
    setGenModal({ month: record.month, concept, total: data?.monthly_fee || '' });
  };

  const submitGenerate = async () => {
    if (!genModal.concept.trim() || !genModal.total) {
      alert('Concept and total are required');
      return;
    }
    try {
      setInvBusy(true);
      await axios.post(`${API}/invoices/generate`, {
        client_id: clientId,
        month: genModal.month,
        concept: genModal.concept.trim(),
        total: parseFloat(genModal.total),
      });
      setGenModal(null);
      await fetchHistory();
    } catch (e) {
      alert('Error generating invoice: ' + (e.response?.data?.detail || 'Unknown error'));
    } finally {
      setInvBusy(false);
    }
  };

  const downloadInvoice = async (inv) => {
    try {
      const res = await axios.get(`${API}/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert('Could not download the PDF');
    }
  };

  const regenerateInvoice = async (inv) => {
    if (!window.confirm(`Regenerate invoice ${inv.invoice_number}? A new version is created and the previous one is archived.`)) return;
    try {
      setInvBusy(true);
      await axios.post(`${API}/invoices/${inv.id}/regenerate`);
      await fetchHistory();
    } catch (e) {
      alert('Error regenerating invoice');
    } finally {
      setInvBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="client-accounting-history">
      {/* Summary info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-blue-900/30 p-3 rounded-lg">
          <div className="text-blue-300 text-xs mb-1">Monthly Fee</div>
          <div className="text-white font-medium">
            {data?.monthly_fee ? `${data.monthly_fee} ${data.currency}` : 'Not set'}
          </div>
        </div>
        <div className="bg-blue-900/30 p-3 rounded-lg">
          <div className="text-blue-300 text-xs mb-1">Mode</div>
          <div className="text-white font-medium">
            {data?.booking_mode === 'monthly_fixed' ? 'Monthly Fixed' :
             data?.booking_mode === 'booking_flex' ? 'Session Package' : '-'}
          </div>
        </div>
        <div className="bg-blue-900/30 p-3 rounded-lg">
          <div className="text-blue-300 text-xs mb-1">Last Renewal</div>
          <div className="text-white font-medium">
            {data?.last_renewal_date ? formatDate(data.last_renewal_date) : '-'}
          </div>
        </div>
        <div className="bg-blue-900/30 p-3 rounded-lg">
          <div className="text-blue-300 text-xs mb-1">Records</div>
          <div className="text-white font-medium">{data?.history?.length || 0}</div>
        </div>
      </div>

      {/* History table */}
      {(!data || !data.history || data.history.length === 0) ? (
        <div className="text-center py-6">
          <p className="text-blue-300 mb-3">No payment history recorded</p>
          {isEditMode && (
            <button
              onClick={createNewMonth}
              disabled={saving}
              className="btn-primary text-sm"
            >
              + Add Current Month
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-blue-400/20">
                <th className="text-left py-2 text-blue-300">Month</th>
                <th className="text-center py-2 text-blue-300">Status</th>
                <th className="text-center py-2 text-blue-300">Payment Date</th>
                <th className="text-left py-2 text-blue-300">Notes</th>
                {isEditMode && <th className="text-center py-2 text-blue-300">Actions</th>}
                <th className="text-center py-2 text-blue-300">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {data.history.map((record, index) => (
                <tr key={record.id || index} className="border-b border-blue-400/10">
                  {editingRow === record.month ? (
                    <>
                      <td className="py-2 text-white font-medium">{formatMonth(record.month)}</td>
                      <td className="py-2 text-center">
                        <select
                          value={editFormData.status}
                          onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                          className="input-field text-xs w-24"
                        >
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="issue">Issue</option>
                        </select>
                      </td>
                      <td className="py-2 text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <input
                            type="date"
                            value={editFormData.payment_date || ''}
                            onChange={(e) => setEditFormData({...editFormData, payment_date: e.target.value})}
                            className="input-field text-xs w-28"
                          />
                          {editFormData.payment_date && (
                            <button
                              type="button"
                              onClick={() => setEditFormData({...editFormData, payment_date: ''})}
                              className="text-red-400 hover:text-red-300 text-xs px-1"
                              title="Clear date"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={editFormData.notes || ''}
                            onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                            className="input-field text-xs flex-1"
                            placeholder="Notes..."
                          />
                          {editFormData.notes && (
                            <button
                              type="button"
                              onClick={() => setEditFormData({...editFormData, notes: ''})}
                              className="text-red-400 hover:text-red-300 text-xs px-1"
                              title="Clear notes"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-2 text-center">
                        <button
                          onClick={() => saveEditing(record.month)}
                          disabled={saving}
                          className="text-green-400 hover:text-green-300 mr-2 font-bold"
                          title="Save changes"
                        >
                          {saving ? '...' : '✓ Save'}
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="text-gray-400 hover:text-gray-300"
                          title="Cancel"
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-2 text-white font-medium">{formatMonth(record.month)}</td>
                      <td className="py-2 text-center">{getStatusBadge(record.status)}</td>
                      <td className="py-2 text-center text-blue-200">{formatDate(record.payment_date)}</td>
                      <td className="py-2 text-blue-300 text-xs">{record.notes || '-'}</td>
                      {isEditMode && (
                        <td className="py-2 text-center">
                          <button
                            onClick={() => startEditing(record)}
                            className="text-blue-400 hover:text-blue-300 mr-2"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteRecord(record.month)}
                            disabled={saving}
                            className="text-red-400 hover:text-red-300"
                            title="Delete this record"
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </>
                  )}
                  <td className="py-2 text-center whitespace-nowrap">
                    {(() => {
                      const inv = invoiceForMonth(record.month);
                      if (inv) {
                        return (
                          <div className="flex items-center gap-1.5 justify-center" data-testid={`invoice-cell-${record.month}`}>
                            <span className="text-blue-200 text-xs font-mono" title={`Version ${inv.version}`}>#{inv.invoice_number}</span>
                            <button onClick={() => downloadInvoice(inv)} className="text-blue-400 hover:text-blue-300 px-1" title="Download PDF" data-testid={`invoice-download-${record.month}`}>⬇︎</button>
                            {isEditMode && (
                              <button onClick={() => regenerateInvoice(inv)} disabled={invBusy} className="text-amber-400 hover:text-amber-300 px-1" title="Regenerate (new version)" data-testid={`invoice-regenerate-${record.month}`}>↻</button>
                            )}
                          </div>
                        );
                      }
                      return (
                        <button onClick={() => openGenerate(record)} className="text-emerald-400 hover:text-emerald-300 text-xs font-medium" data-testid={`invoice-generate-${record.month}`}>
                          Generate
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {isEditMode && (
            <div className="mt-3">
              <button
                onClick={createNewMonth}
                disabled={saving}
                className="btn-secondary text-sm"
              >
                + Add Month Record
              </button>
            </div>
          )}
        </div>
      )}
      
      {!isEditMode && (
        <p className="text-blue-400 text-xs mt-2">
          Click "Edit Client" to modify payment records.
        </p>
      )}

      {genModal && (
        <div className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4" onClick={() => setGenModal(null)} data-testid="generate-invoice-modal">
          <div className="bg-blue-900 rounded-xl max-w-md w-full p-5 max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-1">Generate Invoice</h3>
            <p className="text-blue-300 text-sm mb-4">{formatMonth(genModal.month)}</p>
            <label className="block text-blue-200 text-sm mb-1">Concept (shown on the invoice)</label>
            <input
              type="text"
              value={genModal.concept}
              onChange={(e) => setGenModal({ ...genModal, concept: e.target.value })}
              className="input-field w-full mb-3"
              data-testid="generate-invoice-concept"
            />
            <label className="block text-blue-200 text-sm mb-1">Total (VAT included) €</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={genModal.total}
              onChange={(e) => setGenModal({ ...genModal, total: e.target.value })}
              className="input-field w-full mb-2"
              data-testid="generate-invoice-total"
            />
            {genModal.total ? (
              <p className="text-blue-400 text-xs mb-4">
                Base {(parseFloat(genModal.total) / 1.21).toFixed(2)} € · IVA 21% {(parseFloat(genModal.total) - parseFloat(genModal.total) / 1.21).toFixed(2)} €
              </p>
            ) : <div className="mb-4" />}
            <div className="flex gap-2">
              <button onClick={() => setGenModal(null)} className="btn-secondary flex-1" data-testid="generate-invoice-cancel">Cancel</button>
              <button onClick={submitGenerate} disabled={invBusy} className="btn-primary flex-1" data-testid="generate-invoice-submit">
                {invBusy ? 'Generating...' : 'Generate Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientAccountingHistory;
